import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Upload, Paperclip, X, Check, ArrowRight, ArrowLeft, ArrowUp, Camera, RotateCcw } from 'lucide-react';
import { validaCF } from '../../utils/codiceFiscale';
import { controllaCFPersona, caricaLuoghi, luoghiPronti, suggerisciLuoghi, luogoEsiste, etichettaLuogo, statiEsteri, comuneItaliano, statoDiLuogo } from '../../utils/cfPersona';
import { aspettoCompleto, variabiliTema, COPERTINE, ALTEZZE, riduciFoto } from './formAspetto';
import { FACCINE, campiPersona } from '../../utils/formCampi';
import TestoImpaginato from './TestoImpaginato';
import './FormVista.css';

// Form pubblico con il nuovo aspetto: usato dal link /f/<slug> e dall'anteprima nell'editor.
// I controlli qui aiutano chi compila; quelli che contano li rifà il server.
const MIME_OK = ['application/pdf', 'image/jpeg', 'image/png'];
const FIRMA = { nome: 'B-Cheers Beach Volley Team ASD', sotto: 'Beach Volley Team ASD · dal 2012', voci: ['Affiliata AIBVC', '@bcheersbeachvolley'] };
const leggiBase64 = (file) => new Promise((ok, ko) => {
  const r = new FileReader();
  r.onload = () => ok(String(r.result).split(',')[1] || '');
  r.onerror = () => ko(new Error('Impossibile leggere il file'));
  r.readAsDataURL(file);
});
const RE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const RE_TEL = /^\+?[0-9 ()./-]{6,20}$/;
const SCELTA = ['SCELTA_SINGOLA', 'SI_NO', 'ELENCO'];
const vuoto = (v) => v === undefined || v === null || v === '' || v === false || (Array.isArray(v) && !v.length);
// Firma con il nome: { nome, conferma } nelle risposte (col dito invece è un file negli allegati)
const firmaConNome = (v) => !!v && typeof v === 'object' && !Array.isArray(v) && (!!String(v.nome || '').trim() || !!v.conferma);
const valoreDi = (c, r, al) => c.tipo === 'ALLEGATO' ? al[c.id]
  : c.tipo === 'FIRMA' ? ((al[c.id] || []).length && c.modo_firma !== 'nome' ? al[c.id] : (c.modo_firma && c.modo_firma !== 'dito' && firmaConNome(r[c.id]) ? r[c.id] : undefined))
  : r[c.id];
// Straniero senza CF italiano: la risposta alla domanda del CF diventa { estero: true, stato, sesso, … }
// e la foto del documento va negli allegati della stessa domanda. Il CF lo calcola il server.
const estero = (v) => !!v && typeof v === 'object' && !Array.isArray(v) && v.estero === true;
const DOC_ESTERO = { max_file: 2, max_mb: 5 };
const normNome = (t) => String(t || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z ]/g, ' ').replace(/\s+/g, ' ').trim();

// Domande raggruppate per "Titolo e testo" (SEZIONE); quelle prima della prima sezione stanno in un gruppo senza titolo
const raggruppa = (campi) => {
  const gruppi = [];
  for (const c of campi) {
    if (c.tipo === 'SEZIONE') gruppi.push({ id: c.id, titolo: c.etichetta, descrizione: c.descrizione, campi: [] });
    else {
      if (!gruppi.length) gruppi.push({ id: '_inizio', titolo: null, descrizione: null, campi: [] });
      gruppi[gruppi.length - 1].campi.push(c);
    }
  }
  return gruppi.filter(g => g.campi.length || g.descrizione || g.titolo);
};

let fontFirmaCaricato = false;
const caricaFontFirma = () => {
  if (fontFirmaCaricato) return;
  fontFirmaCaricato = true;
  const l = document.createElement('link');
  l.rel = 'stylesheet';
  l.href = '/fonts/caveat.css';
  document.head.appendChild(l);
};

let fontSerifCaricato = false;
const caricaFontSerif = () => {
  if (fontSerifCaricato) return;
  fontSerifCaricato = true;
  const l = document.createElement('link');
  l.rel = 'stylesheet';
  l.href = '/fonts/fraunces.css';
  document.head.appendChild(l);
};

// Se il modulo chiede anche il codice fiscale, sotto la domanda del nome (senza descrizione propria)
// si ricorda di scrivere anche il secondo nome: il CF si calcola su tutti i nomi (20/10/2026 titolare).
const conAiutoNome = (campi) => {
  const cp = campiPersona(campi);
  if (!cp.nome || !cp.codice_fiscale) return campi;
  return campi.map(c => c.id === cp.nome.id && !c.descrizione ? { ...c, descrizione: "Se hai un secondo nome scrivi anche quello, come sul documento." } : c);
};

export default function FormVista({ form, url = {}, anteprima = false, onInvia }) {
  const a = aspettoCompleto(form.aspetto);
  const campi = useMemo(() => conAiutoNome(form.campi || []), [form.campi]);
  const gruppi = useMemo(() => raggruppa(campi), [campi]);
  // "Testo da leggere" (INFO) sta tra le domande (una alla volta è un passo suo) ma non chiede risposta
  const domande = useMemo(() => campi.filter(c => c.tipo !== 'SEZIONE'), [campi]);
  const daRispondere = useMemo(() => domande.filter(c => c.tipo !== 'INFO'), [domande]);
  const sezioneDi = useMemo(() => {
    const m = {}; let cur = null;
    for (const c of campi) { if (c.tipo === 'SEZIONE') cur = c.etichetta; else m[c.id] = cur; }
    return m;
  }, [campi]);

  const [risposte, setRisposte] = useState({});
  const [allegati, setAllegati] = useState({}); // { campo_id: [{ nome, dati, dimensione }] }
  const [errori, setErrori] = useState({});
  const [avviso, setAvviso] = useState('');
  const [trappola, setTrappola] = useState('');
  const [invio, setInvio] = useState(false);
  const [esito, setEsito] = useState(null);
  const [passo, setPasso] = useState(0);     // una alla volta: 0 = benvenuto, poi una domanda per passo
  const [sezione, setSezione] = useState(0); // a sezioni
  const radice = useRef(null);
  const timerAvanti = useRef(null);

  useEffect(() => { if (a.caratteri === 'serif') caricaFontSerif(); }, [a.caratteri]);
  useEffect(() => () => clearTimeout(timerAvanti.current), []);
  // una alla volta: il cursore va nella risposta, così si scrive e si preme Invio senza toccare lo schermo
  useEffect(() => {
    if (a.modalita !== 'una' || passo < 1 || !radice.current) return;
    const el = radice.current.querySelector('.box input.fv-in, .box textarea.fv-in, .box select.fv-in, .box .fv-tile, .box .fv-chip, .box .fv-cons input');
    if (el) el.focus({ preventScroll: true });
  }, [a.modalita, passo]);
  // cambiando impaginazione nell'anteprima si riparte dall'inizio
  useEffect(() => { setPasso(0); setSezione(0); setErrori({}); setAvviso(''); }, [a.modalita]);

  // domande che descrivono la persona: servono al controllo completo del codice fiscale
  const cp = useMemo(() => campiPersona(campi), [campi]);
  const valoreAnagrafica = (k) => (cp[k] ? risposte[cp[k].id] : undefined);
  const persona = { nome: valoreAnagrafica('nome'), cognome: valoreAnagrafica('cognome'), data_nascita: valoreAnagrafica('data_nascita'), luogo_nascita: valoreAnagrafica('luogo_nascita') };
  const ruoloDi = (id) => Object.keys(cp).find(k => cp[k].id === id);
  const [, setLuoghiOk] = useState(!!luoghiPronti());
  const idLuogo = cp.luogo_nascita?.id;
  useEffect(() => { if (idLuogo || cp.codice_fiscale) caricaLuoghi().then(e => setLuoghiOk(!!e)); }, [idLuogo, cp.codice_fiscale]);
  const cfEstero = cp.codice_fiscale && estero(risposte[cp.codice_fiscale.id]) ? risposte[cp.codice_fiscale.id] : null;

  const erroreCampo = (c, r = risposte, al = allegati) => {
    if (c.tipo === 'SEZIONE' || c.tipo === 'INFO') return null;
    const v = valoreDi(c, r, al);
    // firma con il nome: si controlla anche se la domanda è facoltativa, una volta iniziata
    if (c.tipo === 'FIRMA' && firmaConNome(v)) {
      if (!v.conferma) return 'Spunta la dichiarazione per firmare con il nome.';
      if (normNome(v.nome).split(' ').filter(p => p.length >= 2).length < 2) return 'Scrivi nome e cognome di chi firma.';
      if (c.firma_coincide && persona.nome && persona.cognome) {
        const scritto = ` ${normNome(v.nome)} `;
        if ([persona.nome, persona.cognome].some(p => !scritto.includes(` ${normNome(p)} `))) return `Il nome scritto deve essere ${persona.nome} ${persona.cognome}, come nel modulo.`;
      }
      return null;
    }
    if (vuoto(v)) {
      if (!c.obbligatorio) return null;
      if (c.tipo === 'CONSENSO') return 'Serve il consenso per proseguire.';
      if (c.tipo === 'DOCUMENTO') return c.leggi_tutto === false ? 'Va accettato per proseguire.' : 'Leggi il documento fino in fondo e spunta la casella per accettarlo.';
      if (c.tipo === 'ALLEGATO') return 'Il file è obbligatorio: caricalo per continuare.';
      if (c.tipo === 'FIRMA') return c.modo_firma === 'nome' ? 'Manca la firma: scrivi nome e cognome e spunta la dichiarazione.' : c.modo_firma === 'scelta' ? 'Manca la firma: firma col dito oppure scrivi il nome.' : 'Manca la firma: firma nel riquadro.';
      if (c.tipo === 'VALUTAZIONE') return 'Scegli un voto.';
      if (c.tipo === 'SCELTA_MULTIPLA') return 'Scegli almeno un\'opzione.';
      if (SCELTA.includes(c.tipo)) return 'Scegli un\'opzione.';
      return 'Questa risposta è obbligatoria.';
    }
    if (c.tipo === 'EMAIL' && !RE_EMAIL.test(String(v).trim())) return 'Scrivi un indirizzo email valido.';
    if (c.tipo === 'TELEFONO' && !RE_TEL.test(String(v).trim())) return 'Scrivi un numero di telefono valido.';
    if (c.tipo === 'NUMERO') {
      const n = Number(String(v).replace(',', '.'));
      if (!isFinite(n)) return 'Scrivi un numero.';
      if (c.min !== null && c.min !== undefined && n < c.min) return `Il valore minimo è ${c.min}.`;
      if (c.max !== null && c.max !== undefined && n > c.max) return `Il valore massimo è ${c.max}.`;
    }
    if (c.tipo === 'CODICE_FISCALE' && estero(v)) {
      if (!v.stato) return 'Scegli lo stato di nascita.';
      if (!v.sesso) return 'Indica il sesso.';
      if (!cp.data_nascita && !/^\d{4}-\d{2}-\d{2}$/.test(v.data_nascita || '')) return 'Scrivi la data di nascita.';
      if (!v.documento_tipo) return 'Scegli il tipo di documento.';
      if (!/^[A-Z0-9]{5,20}$/.test(String(v.documento_numero || '').toUpperCase().replace(/[\s-]/g, ''))) return 'Scrivi il numero del documento (solo lettere e numeri).';
      if (!(al[c.id] || []).length) return 'Carica la foto del documento (fronte e retro).';
      if (!persona.nome || !persona.cognome) return 'Scrivi prima nome e cognome.';
      return null;
    }
    if (c.tipo === 'CODICE_FISCALE') {
      if (cp.codice_fiscale?.id === c.id) {
        const es = controllaCFPersona(v, { nome: valoreAnagrafica('nome'), cognome: valoreAnagrafica('cognome'), data_nascita: valoreAnagrafica('data_nascita'), luogo_nascita: valoreAnagrafica('luogo_nascita') });
        if (!es.valido && es.campo === 'cf') return es.errore;
      } else {
        const es = validaCF(v);
        if (!es.valido) return es.errore;
      }
    }
    if (cp.luogo_nascita?.id === c.id && cfEstero && luoghiPronti()) {
      if (comuneItaliano(v)) return 'Risulti nato in Italia: in questo caso hai un codice fiscale, scrivilo nella sua domanda.';
      const st = statoDiLuogo(v);
      if (st && cfEstero.stato && st.nome !== cfEstero.stato) return `Il luogo di nascita non coincide con lo stato scelto per il documento (${etichettaLuogo(st)}).`;
      return null;
    }
    if (cp.luogo_nascita?.id === c.id && luoghiPronti() && !luogoEsiste(v)) return 'Non trovo questo luogo: sceglilo dall\'elenco che compare mentre scrivi. Se sei nato/a all\'estero scrivi lo Stato (es. Svizzera, Francia).';
    return null;
  };

  const controlla = (lista) => {
    const e = {};
    for (const c of lista) { const m = erroreCampo(c); if (m) e[c.id] = m; }
    setErrori(prev => {
      const n = { ...prev };
      for (const c of lista) delete n[c.id];
      return { ...n, ...e };
    });
    return Object.keys(e);
  };

  const set = (c, v) => {
    setRisposte(r => ({ ...r, [c.id]: v }));
    setErrori(e => { if (!e[c.id]) return e; const n = { ...e }; delete n[c.id]; return n; });
    setAvviso('');
  };

  const aggiungiFile = async (c, files, daFotocamera = false) => {
    setAvviso('');
    const lista = [...(allegati[c.id] || [])];
    let msg = '';
    for (const f of files) {
      if (lista.length >= (c.max_file || 1)) { msg = `Puoi caricare al massimo ${c.max_file || 1} file.`; break; }
      const limite = (c.max_mb || 5) * 1024 * 1024;
      // foto del telefono: si alleggerisce (e diventa JPG) invece di scartarla perché pesante o in formato strano
      if ((f.type || '').startsWith('image/') && (daFotocamera || f.size > limite || !MIME_OK.includes(f.type))) {
        try {
          const b64 = (await riduciFoto(f, 2000)).dati.split(',')[1] || '';
          const dim = Math.floor(b64.length * 3 / 4);
          if (dim > limite) { msg = `${f.name}: troppo grande anche dopo averla alleggerita (massimo ${c.max_mb || 5} MB).`; continue; }
          const base = daFotocamera ? `foto-${lista.length + 1}` : (f.name || 'foto').replace(/\.[^.]+$/, '');
          lista.push({ nome: `${base}.jpg`, dati: b64, dimensione: dim });
        } catch { msg = `${f.name || 'Foto'}: formato non ammesso, carica un PDF, JPG o PNG.`; }
        continue;
      }
      if (!MIME_OK.includes(f.type)) { msg = `${f.name}: formato non ammesso, carica un PDF, JPG o PNG.`; continue; }
      if (f.size > (c.max_mb || 5) * 1024 * 1024) { msg = `${f.name}: troppo grande (massimo ${c.max_mb || 5} MB).`; continue; }
      try { lista.push({ nome: f.name, dati: await leggiBase64(f), dimensione: f.size }); } catch (e) { msg = e.message; }
    }
    setAllegati(al => ({ ...al, [c.id]: lista }));
    setErrori(e => { const n = { ...e }; if (msg) n[c.id] = msg; else delete n[c.id]; return n; });
  };
  const setFirma = (c, firma) => {
    setAllegati(al => ({ ...al, [c.id]: firma ? [firma] : [] }));
    setErrori(e => { if (!e[c.id]) return e; const n = { ...e }; delete n[c.id]; return n; });
    setAvviso('');
  };
  const togliFile = (c, i) => setAllegati(al => ({ ...al, [c.id]: (al[c.id] || []).filter((_, j) => j !== i) }));

  const vaiAlPrimoErrore = () => setTimeout(() => {
    const el = radice.current?.querySelector('.fv-q.bad');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, 30);
  const suInizio = () => {
    const el = radice.current;
    if (!el) return;
    if (anteprima) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    else window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const invia = async () => {
    const sbagliate = controlla(domande);
    if (sbagliate.length) {
      // a sezioni / una alla volta: si torna al primo punto da sistemare
      if (a.modalita === 'sezioni') { const g = gruppi.findIndex(g => g.campi.some(c => sbagliate.includes(c.id))); if (g >= 0) setSezione(g); }
      if (a.modalita === 'una') { const i = domande.findIndex(c => sbagliate.includes(c.id)); if (i >= 0) setPasso(i + 1); }
      setAvviso(a.modalita === 'pagina' ? 'Controlla le risposte segnate in rosso.' : '');
      vaiAlPrimoErrore();
      return;
    }
    const totale = Object.values(allegati).flat().reduce((t, f) => t + f.dimensione, 0);
    if (totale > (form.max_mb_totale || 12) * 1024 * 1024) { setAvviso(`Gli allegati superano in totale ${form.max_mb_totale || 12} MB.`); return; }
    if (anteprima) { setEsito({ codice: 'PROVA-ANTEPRIMA', messaggio: form.messaggio_conferma, anteprima: true }); suInizio(); return; }
    setInvio(true);
    try {
      const allegatiInvio = Object.fromEntries(Object.entries(allegati).map(([id, l]) => [id, l.map(({ nome, dati }) => ({ nome, dati }))]));
      const r = await onInvia({ risposte, allegati: allegatiInvio, sito_web: trappola });
      setEsito(r || { ok: true });
      suInizio();
    } catch (err) {
      setAvviso(err.response?.data?.error || err.message || 'Invio non riuscito, riprova.');
    } finally { setInvio(false); }
  };

  const avanti = () => {
    clearTimeout(timerAvanti.current);
    if (a.modalita === 'sezioni') {
      if (controlla(gruppi[sezione].campi).length) { vaiAlPrimoErrore(); return; }
      setSezione(s => s + 1); suInizio();
    }
    if (a.modalita === 'una') {
      if (passo > 0 && controlla([domande[passo - 1]]).length) return;
      setPasso(p => p + 1);
    }
  };
  const indietro = () => {
    clearTimeout(timerAvanti.current);
    setAvviso('');
    if (a.modalita === 'sezioni') { setSezione(s => Math.max(0, s - 1)); suInizio(); }
    else setPasso(p => Math.max(0, p - 1));
  };
  // una alla volta: scelta fatta = si va avanti da soli
  const sceltaFatta = (c) => {
    if (a.modalita !== 'una') return;
    clearTimeout(timerAvanti.current);
    timerAvanti.current = setTimeout(() => setPasso(p => (domande[p - 1]?.id === c.id ? p + 1 : p)), 280);
  };

  const ricomincia = () => { setEsito(null); setRisposte({}); setAllegati({}); setErrori({}); setAvviso(''); setPasso(0); setSezione(0); };

  // ── Pezzi comuni ──
  const stile = variabiliTema(a, url);
  const [hPc, hTel] = ALTEZZE[a.copertina_altezza] || ALTEZZE.media;
  const conFoto = a.copertina === 'foto' && url.copertina;
  const senzaCopertina = a.copertina === 'nessuna' || (a.copertina === 'foto' && !url.copertina);
  // slogan (modificabile nella scheda Aspetto): sulla copertina, nel benvenuto "una alla volta" e nella pagina grazie
  const slogan = a.slogan ? [a.slogan_1, a.slogan_2].map(x => (x || '').trim()).filter(Boolean).join(' ') : '';
  const mostraSlogan = !!slogan && !senzaCopertina;
  const copertina = (
    <div className={`fv-cover${senzaCopertina ? ' nessuna' : ''}${conFoto ? ' foto' : ''}${a.copertina === 'spiaggia' ? ' chiara' : ''}${mostraSlogan ? '' : ' senza-slogan'}`}
      style={{ '--cov-h': `${hPc}px`, '--cov-h-tel': `${hTel}px`, '--cov': conFoto ? `url("${url.copertina}")` : (COPERTINE[a.copertina] || 'none'), backgroundPosition: conFoto ? `${a.copertina_pos.x}% ${a.copertina_pos.y}%` : 'center' }}>
      {mostraSlogan && <div className="fv-slogan">{a.slogan_1}{a.slogan_1 && a.slogan_2 && <br />}<b>{a.slogan_2}</b></div>}
    </div>
  );
  const testata = <>
    <div className="fv-logo"><div className="mk"><img src="/logo.png" alt="B-Cheers" /></div><div className="nm">{FIRMA.sotto}</div></div>
    <h1 className="fv-t">{form.titolo}</h1>
    {form.descrizione && <p className="fv-d">{form.descrizione}</p>}
    {a.etichette?.length > 0 && <div className="fv-meta">{a.etichette.map((e, i) => <span key={i}>{e}</span>)}</div>}
  </>;
  const firma = <div className="fv-firma"><span><b>{FIRMA.nome}</b></span>{FIRMA.voci.map(v => <span key={v}>{v}</span>)}</div>;
  const testoInvio = a.testo_invio || 'Invia';
  const trappolaInput = <input type="text" name="sito_web" value={trappola} onChange={e => setTrappola(e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }} />;
  const classe = `fv ${a.caratteri === 'moderno' ? 'moderno' : ''} ${a.caratteri === 'serif' ? 'serif' : ''} ${a.campi === 'linea' ? 'linea' : ''} ${a.campi === 'senzacard' ? 'senzacard' : ''} ${anteprima ? 'in-anteprima' : ''}`;

  const domanda = (c, grande = false) => (
    <Domanda key={c.id} c={c} grande={grande} valore={risposte[c.id]} errore={errori[c.id]} files={allegati[c.id] || []}
      set={v => { set(c, v); if (c.tipo === 'CODICE_FISCALE' && !estero(v) && (allegati[c.id] || []).length) setAllegati(al => ({ ...al, [c.id]: [] })); }}
      aggiungi={(f, cam) => aggiungiFile(c.tipo === 'CODICE_FISCALE' ? { ...c, ...DOC_ESTERO } : c, f, cam)} togli={i => togliFile(c, i)} onScelta={() => sceltaFatta(c)}
      setFirma={v => setFirma(c, v)} fotoUrl={url.foto}
      persona={persona} ruolo={ruoloDi(c.id)} chiediData={!cp.data_nascita} />
  );

  // ── Pagina "grazie" ──
  if (esito) {
    const nome = valoreAnagrafica('nome') || risposte[campi.find(c => c.tipo === 'TESTO_BREVE' && /^nome$/i.test(c.etichetta.trim()))?.id];
    const riepilogo = domande.filter(c => [...SCELTA, 'SCELTA_MULTIPLA', 'ALLEGATO'].includes(c.tipo)).map(c => {
      const v = c.tipo === 'ALLEGATO' ? (allegati[c.id] || []).map(f => f.nome) : risposte[c.id];
      if (vuoto(v)) return null;
      return [c.etichetta, Array.isArray(v) ? v.join(', ') : v === 'SI' ? 'Sì' : v === 'NO' ? 'No' : String(v)];
    }).filter(Boolean).slice(0, 10);
    return (
      <div className={classe} style={stile} ref={radice}>
        {copertina}
        <div className="fv-inner">
          <div className="fv-blk fv-thanks" style={{ marginTop: senzaCopertina ? 20 : -40, position: 'relative', zIndex: 2 }}>
            <div className="ok"><Check size={34} strokeWidth={3} /></div>
            <h1 className="fv-t">{nome ? `Grazie ${nome}!` : 'Risposta inviata'}</h1>
            <p className="fv-d">{esito.messaggio || 'Grazie, abbiamo ricevuto le tue risposte.'}</p>
            {esito.codice && <>
              <div className="fv-code">{esito.codice}</div>
              <p className="fv-d" style={{ fontSize: 13 }}>{esito.email_inviata_a ? `Il codice ti arriva anche per email a ${esito.email_inviata_a}.` : 'Conserva questo codice: fai uno screenshot o annotalo.'}</p>
            </>}
            {riepilogo.length > 0 && <dl className="fv-riep">{riepilogo.map(([k, v]) => <React.Fragment key={k}><dt>{k}</dt><dd>{v}</dd></React.Fragment>)}</dl>}
            {slogan && <p className="fv-d" style={{ marginTop: 20 }}>{slogan.replace(/\.$/, '!')}</p>}
            {esito.anteprima && <div style={{ marginTop: 18 }}><button type="button" className="fv-btn gh" onClick={ricomincia}>Ricomincia la prova</button></div>}
          </div>
          {firma}
        </div>
      </div>
    );
  }

  // ── Form chiuso (fuori date, posti finiti…) ──
  if (form.chiuso) return (
    <div className={classe} style={stile} ref={radice}>
      {copertina}
      <div className="fv-inner">{testata}<div className="fv-chiuso">{form.chiuso}</div>{firma}</div>
    </div>
  );

  // ── Una domanda alla volta ──
  if (a.modalita === 'una' && domande.length) {
    const tot = domande.length, ult = passo === tot;
    const c = passo > 0 ? domande[passo - 1] : null;
    const tasto = (e) => {
      if (e.key !== 'Enter' || e.shiftKey || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'BUTTON' || e.target.type === 'file') return;
      e.preventDefault();
      if (ult) invia(); else avanti();
    };
    return (
      <div className={classe} style={stile} ref={radice} onKeyDown={tasto}>
        <div className="fv-one">
          <div className="fv-bar"><i style={{ width: `${(passo / tot) * 100}%` }} /></div>
          <div className="head"><div className="mk"><img src="/logo.png" alt="" /></div><span>B-Cheers Beach Volley</span>{passo > 0 && <span className="cnt">{passo} / {tot}</span>}</div>
          <div className="body">
            {passo === 0 ? (
              <div className="box" key="benvenuto">
                <img src="/logo.png" alt="B-Cheers" style={{ height: 60, marginBottom: 14 }} />
                {slogan && <div className="num">{slogan.replace(/[.!]$/, '')}</div>}
                <div className="fv-ql">{form.titolo}</div>
                {form.descrizione && <p className="fv-qd" style={{ maxWidth: '54ch' }}>{form.descrizione}</p>}
                {a.etichette?.length > 0 && <div className="fv-meta">{a.etichette.map((e, i) => <span key={i}>{e}</span>)}</div>}
                <div className="fv-foot" style={{ justifyContent: 'flex-start', marginTop: 24 }}>
                  <button type="button" className="fv-btn" onClick={avanti} autoFocus={!anteprima}>Iniziamo <ArrowRight size={18} /></button>
                  <span className="fv-hint">oppure premi <kbd>Invio ↵</kbd></span>
                </div>
              </div>
            ) : (
              <div className="box" key={c.id}>
                {trappolaInput}
                <div className="num">{passo}{sezioneDi[c.id] ? ` → ${sezioneDi[c.id]}` : ''}</div>
                {domanda(c, true)}
                {avviso && <div className="fv-avviso" role="alert">{avviso}</div>}
                <div className="fv-foot" style={{ justifyContent: 'flex-start', marginTop: 26 }}>
                  <button type="button" className="fv-btn" onClick={ult ? invia : avanti} disabled={invio}>
                    {ult ? (invio ? 'Invio in corso…' : <>{testoInvio} <ArrowRight size={18} /></>) : c.tipo === 'INFO' ? <>Avanti <ArrowRight size={18} /></> : <>OK <Check size={18} /></>}
                  </button>
                  <span className="fv-hint">premi <kbd>Invio ↵</kbd></span>
                  <button type="button" className="fv-btn gh" style={{ marginLeft: 'auto' }} onClick={indietro}><ArrowUp size={16} /> Indietro</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── A sezioni ──
  if (a.modalita === 'sezioni' && gruppi.length > 1) {
    const g = gruppi[sezione], ult = sezione === gruppi.length - 1;
    return (
      <div className={classe} style={stile} ref={radice}>
        {copertina}
        <div className="fv-inner">
          {testata}
          <div className="fv-steps">{gruppi.map((x, i) => (
            <div key={x.id} className={i === sezione ? 'on' : i < sezione ? 'done' : ''}><i /><span>{i + 1}. {x.titolo || 'Inizio'}</span></div>
          ))}</div>
          {g.titolo && <div className="fv-sec">{g.titolo}</div>}
          {g.descrizione && <p className="fv-sec-d">{g.descrizione}</p>}
          {g.campi.length > 0 && <div className="fv-blk">{trappolaInput}{g.campi.map(c => domanda(c))}</div>}
          {avviso && <div className="fv-avviso" role="alert">{avviso}</div>}
          <div className="fv-foot">
            {sezione > 0 ? <button type="button" className="fv-btn gh" onClick={indietro}><ArrowLeft size={16} /> Indietro</button> : <span />}
            <button type="button" className="fv-btn" onClick={ult ? invia : avanti} disabled={invio}>
              {ult ? (invio ? 'Invio in corso…' : <>{testoInvio} <ArrowRight size={18} /></>) : <>Continua <ArrowRight size={18} /></>}
            </button>
          </div>
          {firma}
        </div>
      </div>
    );
  }

  // ── Pagina unica (anche ripiego se non ci sono sezioni o domande) ──
  const fatte = daRispondere.filter(c => !vuoto(valoreDi(c, risposte, allegati)) && !erroreCampo(c)).length;
  return (
    <div className={classe} style={stile} ref={radice}>
      {copertina}
      <div className="fv-inner">
        {testata}
        {daRispondere.length > 0 && <div className="fv-bar" style={{ marginTop: 16 }}><i style={{ width: `${(fatte / daRispondere.length) * 100}%` }} /></div>}
        {trappolaInput}
        {gruppi.map(g => (
          <React.Fragment key={g.id}>
            {g.titolo && <div className="fv-sec">{g.titolo}</div>}
            {g.descrizione && <p className="fv-sec-d">{g.descrizione}</p>}
            {g.campi.length > 0 && <div className="fv-blk">{g.campi.map(c => domanda(c))}</div>}
          </React.Fragment>
        ))}
        {avviso && <div className="fv-avviso" role="alert">{avviso}</div>}
        {domande.length > 0 && (
          <div className="fv-foot">
            <span className="fv-prog">{daRispondere.length ? `${fatte} di ${daRispondere.length} risposte` : ''}</span>
            <button type="button" className="fv-btn" onClick={invia} disabled={invio}>{invio ? 'Invio in corso…' : <>{testoInvio} <ArrowRight size={18} /></>}</button>
          </div>
        )}
        {firma}
      </div>
    </div>
  );
}

const TELEFONO = typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches;

function Domanda({ c, grande, valore, errore, files, set, aggiungi, togli, onScelta, persona, ruolo, setFirma, fotoUrl, chiediData }) {
  const id = `fv_${c.id}`;
  const [sopra, setSopra] = useState(false);
  const [sopraVoto, setSopraVoto] = useState(0);
  const etichetta = grande
    ? <><div className="fv-ql">{c.tipo === 'CONSENSO' ? 'Ultima cosa' : c.etichetta}{c.obbligatorio && c.tipo !== 'CONSENSO' && <em> *</em>}</div>{c.descrizione && c.tipo !== 'CONSENSO' && <div className="fv-qd">{c.descrizione}</div>}</>
    : c.tipo === 'CONSENSO' ? null
      : <><label className="fv-ql" htmlFor={['SCELTA_SINGOLA', 'SCELTA_MULTIPLA', 'SI_NO', 'ALLEGATO', 'FIRMA', 'VALUTAZIONE', 'DOCUMENTO', 'INFO'].includes(c.tipo) ? undefined : id}>{c.etichetta}{c.obbligatorio && <em> *</em>}</label>{c.descrizione && <div className="fv-qd">{c.descrizione}</div>}</>;
  const note = c.note_opzioni || [];
  const foto = c.foto_opzioni || [];
  const conFoto = !!fotoUrl && foto.some(Boolean);
  // con le foto: immagine sopra, testo sotto (se un'opzione non ha foto resta un riquadro neutro)
  const dentro = (o, k) => conFoto
    ? <><span className="ph">{foto[k] && fotoUrl(foto[k]) && <img src={fotoUrl(foto[k])} alt="" loading="lazy" draggable={false} />}</span><span className="tx"><b>{o}</b>{note[k] && <small>{note[k]}</small>}</span></>
    : <><b>{o}</b>{note[k] && <small>{note[k]}</small>}</>;
  const sel = Array.isArray(valore) ? valore : [];
  let campo = null;

  if (c.tipo === 'TESTO_BREVE' && ruolo === 'luogo_nascita') campo = <CampoLuogo id={id} valore={valore} set={set} />;
  else if (c.tipo === 'TESTO_BREVE') campo = <input id={id} className="fv-in" value={valore || ''} maxLength={300} onChange={e => set(e.target.value)} autoComplete={c.anagrafica === 'nome' ? 'given-name' : c.anagrafica === 'cognome' ? 'family-name' : 'off'} />;
  if (c.tipo === 'TESTO_LUNGO') campo = <textarea id={id} className="fv-in" value={valore || ''} maxLength={5000} onChange={e => set(e.target.value)} />;
  if (c.tipo === 'EMAIL') campo = <input id={id} className="fv-in" type="email" inputMode="email" autoComplete="email" value={valore || ''} onChange={e => set(e.target.value)} placeholder="nome@esempio.it" />;
  if (c.tipo === 'TELEFONO') campo = <input id={id} className="fv-in" type="tel" inputMode="tel" autoComplete="tel" value={valore || ''} onChange={e => set(e.target.value)} />;
  if (c.tipo === 'NUMERO') campo = <input id={id} className="fv-in" type="number" inputMode="decimal" value={valore ?? ''} min={c.min ?? undefined} max={c.max ?? undefined} onChange={e => set(e.target.value)} />;
  if (c.tipo === 'DATA') campo = <input id={id} className="fv-in" type="date" value={valore || ''} onChange={e => set(e.target.value)} />;
  if (c.tipo === 'CODICE_FISCALE' && ruolo === 'codice_fiscale' && estero(valore)) {
    campo = <>
      <SonoStraniero valore={valore} set={set} luogo={persona.luogo_nascita} />
      <CampiStraniero id={id} valore={valore} set={set} files={files} aggiungi={aggiungi} togli={togli} chiediData={chiediData} />
    </>;
  } else if (c.tipo === 'CODICE_FISCALE') {
    const pieno = typeof valore === 'string' && valore.replace(/\s+/g, '').length >= 16;
    const es = pieno ? (ruolo === 'codice_fiscale' ? controllaCFPersona(valore, persona) : validaCF(valore)) : null;
    const controllati = ruolo === 'codice_fiscale' ? [persona.nome && persona.cognome && 'nome e cognome', persona.data_nascita && 'data', persona.luogo_nascita && 'luogo di nascita'].filter(Boolean) : [];
    campo = <>
      <input id={id} className="fv-in fv-cf" value={valore || ''} maxLength={20} autoComplete="off" placeholder="RSSMRA90A01F205X" onChange={e => set(e.target.value)} />
      {es?.valido && !errore && <div className="fv-ok">✓ Codice fiscale corretto{controllati.length ? ` (${controllati.join(', ')})` : ''}{es.sesso ? ` · ${es.sesso === 'F' ? 'femmina' : 'maschio'}` : ''}</div>}
      {ruolo === 'codice_fiscale' && c.stranieri !== false && <SonoStraniero valore={valore} set={set} luogo={persona.luogo_nascita} />}
    </>;
  }
  if (c.tipo === 'ELENCO') campo = (
    <select id={id} className="fv-in" value={valore || ''} onChange={e => { set(e.target.value); if (e.target.value) onScelta(); }}>
      <option value="">Scegli…</option>
      {c.opzioni.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
  if (c.tipo === 'SCELTA_SINGOLA') campo = (
    <div className={`fv-tiles${conFoto ? ' foto' : ''}`} role="radiogroup" aria-label={c.etichetta}>
      {c.opzioni.map((o, k) => (
        <button key={o} type="button" role="radio" className="fv-tile" aria-checked={valore === o} onClick={() => { set(o); onScelta(); }}>
          {dentro(o, k)}
        </button>
      ))}
    </div>
  );
  if (c.tipo === 'SI_NO') campo = (
    <div className="fv-tiles" role="radiogroup" aria-label={c.etichetta} style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', maxWidth: 360 }}>
      {[['SI', 'Sì'], ['NO', 'No']].map(([v, l]) => (
        <button key={v} type="button" role="radio" className="fv-tile" aria-checked={valore === v} onClick={() => { set(v); onScelta(); }}><b>{l}</b></button>
      ))}
    </div>
  );
  if (c.tipo === 'SCELTA_MULTIPLA') {
    const cambia = (o) => set(sel.includes(o) ? sel.filter(x => x !== o) : c.opzioni.filter(x => x === o || sel.includes(x)));
    campo = note.some(Boolean) || conFoto
      ? <div className={`fv-tiles${conFoto ? ' foto' : ''}`} role="group" aria-label={c.etichetta}>{c.opzioni.map((o, k) => (
          <button key={o} type="button" role="checkbox" className="fv-tile" aria-checked={sel.includes(o)} onClick={() => cambia(o)}>{dentro(o, k)}</button>
        ))}</div>
      : <div className="fv-chips" role="group" aria-label={c.etichetta}>{c.opzioni.map(o => (
          <button key={o} type="button" role="checkbox" className="fv-chip" aria-checked={sel.includes(o)} onClick={() => cambia(o)}>{o}</button>
        ))}</div>;
  }
  if (c.tipo === 'VALUTAZIONE') campo = c.stile === 'faccine'
    ? <div className="fv-voto facce" role="radiogroup" aria-label={c.etichetta}>
        {FACCINE.map(([e, l], k) => (
          <button key={k} type="button" role="radio" aria-checked={valore === k + 1} aria-label={l} className={valore === k + 1 ? 'on' : ''} onClick={() => { set(k + 1); onScelta(); }}>
            <span className="e" aria-hidden="true">{e}</span><small>{l}</small>
          </button>
        ))}
      </div>
    : <div className="fv-voto stelle" role="radiogroup" aria-label={c.etichetta} onMouseLeave={() => setSopraVoto(0)}>
        {[1, 2, 3, 4, 5].map(n => (
          <button key={n} type="button" role="radio" aria-checked={valore === n} aria-label={`${n} su 5`} className={(sopraVoto || valore || 0) >= n ? 'on' : ''}
            onMouseEnter={() => setSopraVoto(n)} onClick={() => { set(n); onScelta(); }}>★</button>
        ))}
        {valore > 0 && <span className="lbl">{valore} su 5</span>}
      </div>;
  if (c.tipo === 'FIRMA') campo = <FirmaCampo c={c} firma={files[0]} setFirma={setFirma} valore={valore} set={set} persona={persona} />;
  if (c.tipo === 'INFO') campo = (
    <div className="fv-info">
      {c.testo_doc && <TestoImpaginato testo={c.testo_doc} />}
      {(c.pagine_doc || []).map((n, k) => fotoUrl && fotoUrl(n) ? <img key={n} src={fotoUrl(n)} alt={`${c.etichetta} ${k + 1}`} loading="lazy" draggable={false} /> : null)}
    </div>
  );
  if (c.tipo === 'DOCUMENTO') campo = <Documento c={c} id={id} valore={valore} set={set} fotoUrl={fotoUrl} />;
  if (c.tipo === 'CONSENSO') campo = (
    <label className="fv-cons" htmlFor={id}>
      <input id={id} type="checkbox" checked={!!valore} onChange={e => set(e.target.checked)} />
      <span>{c.etichetta}{c.obbligatorio && <em className="fv-req"> *</em>}{c.descrizione && <span className="fv-qd" style={{ display: 'block', marginTop: 4 }}>{c.descrizione}</span>}
        {/privacy|informativa|dati personali/i.test(c.etichetta) && <a href="/privacy" target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ display: 'block', marginTop: 4, color: 'var(--a2)', fontWeight: 600 }}>Leggi l'informativa privacy</a>}
      </span>
    </label>
  );
  if (c.tipo === 'ALLEGATO') {
    const max = c.max_file || 1;
    campo = <div className="fv-files">
      {files.map((f, i) => (
        <div key={i} className="fv-file">
          <Paperclip size={16} /><span className="n">{f.nome}</span><small>{(f.dimensione / 1024 / 1024).toFixed(1)} MB</small>
          <button type="button" onClick={() => togli(i)} aria-label={`Togli ${f.nome}`}><X size={16} /></button>
        </div>
      ))}
      {files.length < max && TELEFONO && (
        <label className="fv-drop" htmlFor={`${id}_cam`}>
          <span className="ic"><Camera size={22} /></span>
          <span><b>Scatta foto</b><small>Fotografa il documento con il telefono</small></span>
          <input id={`${id}_cam`} type="file" accept="image/*" capture="environment"
            onChange={e => { aggiungi([...e.target.files], true); e.target.value = ''; }} />
        </label>
      )}
      {files.length < max && (
        <label className={`fv-drop${sopra ? ' sopra' : ''}`} htmlFor={id}
          onDragOver={e => { e.preventDefault(); setSopra(true); }} onDragLeave={() => setSopra(false)}
          onDrop={e => { e.preventDefault(); setSopra(false); aggiungi([...e.dataTransfer.files]); }}>
          <span className="ic"><Upload size={22} /></span>
          <span><b>{files.length ? 'Aggiungi un altro file' : TELEFONO ? 'Scegli un file o una foto' : 'Carica il file'}</b>
            <small>Trascinalo qui o tocca per sceglierlo · PDF, JPG o PNG fino a {c.max_mb || 5} MB{max > 1 ? ` · fino a ${max} file` : ''}</small></span>
          <input id={id} type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" multiple={max > 1}
            onChange={e => { aggiungi([...e.target.files]); e.target.value = ''; }} />
        </label>
      )}
    </div>;
  }

  return (
    <div className={`fv-q${errore ? ' bad' : ''}`} data-q={c.id}>
      {etichetta}
      <div className={grande ? 'fv-campo' : undefined} style={grande ? undefined : { display: 'contents' }}>{campo}</div>
      {c.tipo === 'SCELTA_MULTIPLA' && !errore && <div className="fv-qd" style={{ marginTop: 0 }}>Puoi sceglierne più di una.</div>}
      {errore && <div className="fv-err" role="alert">{errore}</div>}
    </div>
  );
}

// Straniero senza codice fiscale italiano (26/10/2026): casella sotto il CF della persona, poi stato di
// nascita, sesso, documento con foto. Il CF lo calcola il server; lo staff confronta la foto con i dati.
function SonoStraniero({ valore, set, luogo }) {
  const si = estero(valore);
  return (
    <label className="fv-cons fv-estero-si">
      <input type="checkbox" checked={si} onChange={e => set(e.target.checked ? { estero: true, ...(statoDiLuogo(luogo) ? { stato: statoDiLuogo(luogo).nome } : {}) } : '')} />
      <span>Sono straniero/a e non ho un codice fiscale italiano
        <span className="fv-qd" style={{ display: 'block', marginTop: 2 }}>Foreign athlete without an Italian tax code · Athlète étranger sans code fiscal italien</span>
      </span>
    </label>
  );
}

function CampiStraniero({ id, valore, set, files, aggiungi, togli, chiediData }) {
  const [, setPronto] = useState(!!luoghiPronti());
  useEffect(() => { if (!luoghiPronti()) caricaLuoghi().then(e => setPronto(!!e)); }, []);
  const stati = statiEsteri();
  const imposta = (k, x) => set({ ...valore, [k]: x });
  return (
    <div className="fv-estero">
      <p className="fv-qd" style={{ margin: 0 }}>Il codice fiscale lo calcoliamo noi dai tuoi dati. Carica un documento d'identità: lo staff controlla che nome, cognome e data di nascita coincidano. Se sei nato/a in Italia hai un codice fiscale: usa quello.</p>
      <label className="fv-ql" htmlFor={`${id}_stato`}>Stato di nascita <em>*</em></label>
      <select id={`${id}_stato`} className="fv-in" value={valore.stato || ''} onChange={e => imposta('stato', e.target.value)}>
        <option value="">{stati.length ? 'Scegli…' : 'Caricamento…'}</option>
        {stati.map((v, k) => <React.Fragment key={v.codice}>
          <option value={v.nome}>{etichettaLuogo(v)}</option>
          {k === 1 && <option disabled>──────────</option>}
        </React.Fragment>)}
      </select>
      <div className="fv-ql">Sesso <em>*</em></div>
      <div className="fv-tiles" role="radiogroup" aria-label="Sesso" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', maxWidth: 360 }}>
        {[['F', 'Femmina'], ['M', 'Maschio']].map(([v, l]) => (
          <button key={v} type="button" role="radio" className="fv-tile" aria-checked={valore.sesso === v} onClick={() => imposta('sesso', v)}><b>{l}</b></button>
        ))}
      </div>
      {chiediData && <>
        <label className="fv-ql" htmlFor={`${id}_data`}>Data di nascita <em>*</em></label>
        <input id={`${id}_data`} className="fv-in" type="date" value={valore.data_nascita || ''} onChange={e => imposta('data_nascita', e.target.value)} />
      </>}
      <label className="fv-ql" htmlFor={`${id}_doc`}>Documento <em>*</em></label>
      <select id={`${id}_doc`} className="fv-in" value={valore.documento_tipo || ''} onChange={e => imposta('documento_tipo', e.target.value)}>
        <option value="">Scegli…</option>
        <option value="PASSAPORTO">Passaporto</option>
        <option value="CARTA_IDENTITA">Carta d'identità</option>
      </select>
      <label className="fv-ql" htmlFor={`${id}_num`}>Numero del documento <em>*</em></label>
      <input id={`${id}_num`} className="fv-in fv-cf" value={valore.documento_numero || ''} maxLength={30} autoComplete="off" onChange={e => imposta('documento_numero', e.target.value)} />
      <div className="fv-ql">Foto del documento (fronte e retro) <em>*</em></div>
      <div className="fv-files">
        {files.map((f, i) => (
          <div key={i} className="fv-file">
            <Paperclip size={16} /><span className="n">{f.nome}</span><small>{(f.dimensione / 1024 / 1024).toFixed(1)} MB</small>
            <button type="button" onClick={() => togli(i)} aria-label={`Togli ${f.nome}`}><X size={16} /></button>
          </div>
        ))}
        {files.length < DOC_ESTERO.max_file && TELEFONO && (
          <label className="fv-drop" htmlFor={`${id}_cam`}>
            <span className="ic"><Camera size={22} /></span>
            <span><b>Scatta foto</b><small>{files.length ? 'Ora il retro' : 'Fotografa il documento con il telefono'}</small></span>
            <input id={`${id}_cam`} type="file" accept="image/*" capture="environment" onChange={e => { aggiungi([...e.target.files], true); e.target.value = ''; }} />
          </label>
        )}
        {files.length < DOC_ESTERO.max_file && (
          <label className="fv-drop" htmlFor={`${id}_file`}>
            <span className="ic"><Upload size={22} /></span>
            <span><b>{files.length ? 'Aggiungi il retro' : 'Carica il documento'}</b><small>PDF, JPG o PNG fino a {DOC_ESTERO.max_mb} MB · fino a {DOC_ESTERO.max_file} file</small></span>
            <input id={`${id}_file`} type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" multiple onChange={e => { aggiungi([...e.target.files]); e.target.value = ''; }} />
          </label>
        )}
      </div>
    </div>
  );
}

// Firma secondo il modo scelto nell'editor: col dito, scrivendo il nome, oppure a scelta di chi compila.
// Passando da un modo all'altro si toglie la firma dell'altro modo: ne arriva sempre una sola.
function FirmaCampo({ c, firma, setFirma, valore, set, persona }) {
  const modo = c.modo_firma || 'dito';
  const [scelta, setScelta] = useState(firmaConNome(valore) ? 'nome' : 'dito');
  const attivo = modo === 'scelta' ? scelta : modo;
  const cambia = (m) => {
    if (m === scelta) return;
    setScelta(m);
    if (m === 'nome') setFirma(null); else set(undefined);
  };
  const suggerito = persona?.nome && persona?.cognome ? `${persona.nome} ${persona.cognome}`.trim() : '';
  return <>
    {modo === 'scelta' && (
      <div className="fv-sign-modi" role="group" aria-label="Come vuoi firmare">
        {[['dito', '✍ Firmo col dito'], ['nome', 'Aa Scrivo il nome']].map(([k, l]) => (
          <button key={k} type="button" aria-pressed={attivo === k} onClick={() => cambia(k)}>{l}</button>
        ))}
      </div>
    )}
    {attivo === 'dito' ? <Firma firma={firma} setFirma={setFirma} /> : <FirmaNome valore={valore} set={set} suggerito={suggerito} />}
  </>;
}

// Firma con il nome: nome e cognome scritti + dichiarazione che valgono come firma
function FirmaNome({ valore, set, suggerito }) {
  useEffect(() => { caricaFontFirma(); }, []);
  const v = firmaConNome(valore) ? valore : { nome: '', conferma: false };
  const aggiorna = (patch) => {
    const n = { ...v, ...patch };
    set(String(n.nome || '').trim() || n.conferma ? n : undefined);
  };
  return (
    <div className="fv-sign-nome">
      <input className="fv-in" value={v.nome || ''} maxLength={120} autoComplete="name" placeholder={suggerito || 'Nome e cognome'} aria-label="Nome e cognome di chi firma"
        onChange={e => aggiorna({ nome: e.target.value })} />
      <div className="anteprima" aria-hidden="true"><span>{v.nome || ''}</span></div>
      <label className="fv-cons">
        <input type="checkbox" checked={!!v.conferma} onChange={e => aggiorna({ conferma: e.target.checked })} />
        <span>Dichiaro che il nome scritto qui sopra vale come mia firma</span>
      </label>
    </div>
  );
}

// Regolamento da sottoscrivere: il foglio scorre; con "leggi tutto" la spunta si sblocca solo arrivati in fondo.
// Le pagine immagine contano solo quando sono caricate (prima il foglio sembrerebbe corto e "già letto").
function Documento({ c, id, valore, set, fotoUrl }) {
  const foglio = useRef(null);
  const pagine = (c.pagine_doc || []).map(n => ({ n, src: fotoUrl ? fotoUrl(n) : null })).filter(p => p.src);
  const [caricate, setCaricate] = useState(0);
  const [letto, setLetto] = useState(c.leggi_tutto === false || !!valore);
  const pronto = caricate >= pagine.length;
  const controlla = useCallback(() => {
    const el = foglio.current;
    if (!el || !pronto) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight <= 24) setLetto(true);
  }, [pronto]);
  // testo corto (niente da scorrere) = già tutto visibile
  useEffect(() => { if (!letto) controlla(); }, [letto, controlla, c.testo_doc]);
  const caricata = () => setCaricate(n => n + 1);
  return (
    <div className="fv-doc">
      <div className="fv-foglio" ref={foglio} onScroll={letto ? undefined : controlla} tabIndex={0} role="document" aria-label={c.etichetta}>
        {c.testo_doc && <TestoImpaginato testo={c.testo_doc} />}
        {pagine.map((p, k) => <img key={p.n} className="fv-foglio-pag" src={p.src} alt={`Pagina ${k + 1}`} draggable={false} onLoad={caricata} onError={caricata} />)}
      </div>
      {c.leggi_tutto !== false && <div className={`fv-doc-hint${letto ? ' ok' : ''}`}>{letto ? '✓ Letto fino in fondo' : '↓ Scorri il documento fino in fondo per poter accettare'}</div>}
      <label className={`fv-cons${letto ? '' : ' bloccato'}`} htmlFor={id}>
        <input id={id} type="checkbox" checked={!!valore} disabled={!letto} onChange={e => set(e.target.checked)} />
        <span>{c.testo_accetta || 'Ho letto e accetto il regolamento'}{c.obbligatorio && <em className="fv-req"> *</em>}</span>
      </label>
    </div>
  );
}

// Firma col dito (o col mouse): riquadro bianco, ogni tratto aggiorna la firma come PNG.
// Tornando su una domanda già firmata si vede la firma, con "Rifai".
function Firma({ firma, setFirma }) {
  const tela = useRef(null);
  const tratto = useRef(null);
  const [rivedi, setRivedi] = useState(!!firma);
  const [vuota, setVuota] = useState(true);

  useEffect(() => {
    if (rivedi || !tela.current) return;
    const cv = tela.current, dpr = Math.min(window.devicePixelRatio || 1, 2);
    cv.width = Math.round(cv.clientWidth * dpr); cv.height = Math.round(cv.clientHeight * dpr);
    const ctx = cv.getContext('2d');
    ctx.scale(dpr, dpr); ctx.lineWidth = 2.6; ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.strokeStyle = '#111';
  }, [rivedi]);

  // coordinate nel riquadro, anche quando l'anteprima dell'editor è rimpicciolita
  const punto = (e) => {
    const r = tela.current.getBoundingClientRect(), k = tela.current.clientWidth / r.width;
    return [(e.clientX - r.left) * k, (e.clientY - r.top) * k];
  };
  const giu = (e) => {
    e.preventDefault();
    tela.current.setPointerCapture?.(e.pointerId);
    const [x, y] = punto(e);
    const ctx = tela.current.getContext('2d');
    ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x + 0.1, y + 0.1); ctx.stroke();
    tratto.current = [x, y];
  };
  const muovi = (e) => {
    if (!tratto.current) return;
    const [x, y] = punto(e);
    const ctx = tela.current.getContext('2d');
    ctx.lineTo(x, y); ctx.stroke();
    tratto.current = [x, y];
  };
  const su = () => {
    if (!tratto.current) return;
    tratto.current = null;
    setVuota(false);
    // PNG su fondo bianco, così si legge ovunque la si apra
    const cv = tela.current, out = document.createElement('canvas');
    out.width = cv.width; out.height = cv.height;
    const ctx = out.getContext('2d'); ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, out.width, out.height); ctx.drawImage(cv, 0, 0);
    const b64 = out.toDataURL('image/png').split(',')[1] || '';
    setFirma({ nome: 'firma.png', dati: b64, dimensione: Math.floor(b64.length * 3 / 4) });
  };
  const cancella = () => {
    const cv = tela.current;
    if (cv) cv.getContext('2d').clearRect(0, 0, cv.width, cv.height);
    setVuota(true); setFirma(null);
  };

  if (rivedi && firma) return (
    <div className="fv-sign">
      <img src={`data:image/png;base64,${firma.dati}`} alt="La tua firma" />
      <div className="fv-sign-azioni"><span>✓ Firma inserita</span><button type="button" onClick={() => { setFirma(null); setRivedi(false); setVuota(true); }}><RotateCcw size={14} /> Rifai</button></div>
    </div>
  );
  return (
    <div className="fv-sign">
      <canvas ref={tela} onPointerDown={giu} onPointerMove={muovi} onPointerUp={su} onPointerCancel={su} onPointerLeave={su} aria-label="Riquadro per la firma" />
      <div className="riga" aria-hidden="true">{vuota ? '✍ Firma qui con il dito' : ''}</div>
      <div className="fv-sign-azioni"><span>{vuota ? 'Col mouse da computer' : '✓ Firma inserita'}</span>{!vuota && <button type="button" onClick={cancella}><RotateCcw size={14} /> Cancella</button>}</div>
    </div>
  );
}

// Luogo di nascita: si scrive e si sceglie dall'elenco di comuni (anche soppressi) e stati esteri,
// così il nome è quello che il codice fiscale si aspetta.
function CampoLuogo({ id, valore, set }) {
  const [aperto, setAperto] = useState(false);
  const [sel, setSel] = useState(0);
  const [pronto, setPronto] = useState(!!luoghiPronti());
  useEffect(() => { if (!pronto) caricaLuoghi().then(e => setPronto(!!e)); }, [pronto]);
  const lista = aperto && pronto ? suggerisciLuoghi(valore || '') : [];
  const scegli = (v) => { set(etichettaLuogo(v)); setAperto(false); };
  const tasto = (e) => {
    if (!lista.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setSel(i => Math.min(lista.length - 1, i + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSel(i => Math.max(0, i - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); scegli(lista[sel] || lista[0]); }
    else if (e.key === 'Escape') setAperto(false);
  };
  return (
    <div className="fv-luogo">
      <input id={id} className="fv-in" value={valore || ''} maxLength={120} autoComplete="off" placeholder="Comune, o lo Stato se sei nato/a all'estero…"
        onChange={e => { set(e.target.value); setAperto(true); setSel(0); }} onFocus={() => setAperto(true)} onBlur={() => setTimeout(() => setAperto(false), 150)} onKeyDown={tasto}
        role="combobox" aria-expanded={lista.length > 0} aria-autocomplete="list" />
      {lista.length > 0 && (
        <ul role="listbox">
          {lista.map((v, i) => (
            <li key={v.codice + v.nome + v.prov} role="option" aria-selected={i === sel} className={i === sel ? 'on' : ''} onMouseDown={e => { e.preventDefault(); scegli(v); }} onMouseEnter={() => setSel(i)}>
              <b>{etichettaLuogo(v)}</b>{v.prov === 'EE' ? <small>stato estero</small> : !v.attivo ? <small>comune soppresso</small> : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
